/**
 * Version History display mapping (#1148, Phase 2).
 * Maps a backend delta entry to the mockup's SECTION NAME + FIELD NAME and formats values,
 * and maps the machine `source` to its badge label. Section names resolve through the real
 * field registry (`fieldMappingService`) so they reflect actual request sections.
 *
 * @see _bmad-output/planning-artifacts/ux-1148-version-history/ux-1148-version-history.md
 */

import { fieldMappingService } from '../config/fieldMapping';
import { convertToPlainText } from './richContent/convertToPlainText';
import {
  isLegacyPlainTextAreaValue,
  sanitizeTextArea,
} from './richContent/sanitizeTextArea';
import {
  historyUnitCode,
  isDimensionField,
  parseDimensionUnits,
} from '../request/dimensionUnitChoices';
import { getRegistryFieldLabel } from '../request/fields/requestFieldRegistry';

import type {
  RequestVersionDelta,
  RequestVersionSource,
} from '../types/RequestVersion';

/** Child collection key → human section name (aligned to fieldMapping section vocabulary). */
const ARRAY_KEY_TO_SECTION: Record<string, string> = {
  rackConfigurations: 'Racks',
  smallUpsConfigurations: 'Small UPS',
  coolingSystemConfigurations: 'Cooling Readiness',
  liquidCoolingCduConfigurations: 'Liquid Cooling',
  largeUpsConfigurations: 'Large UPS',
  // Power Distribution changes belong to their own section, not Facility Power Readiness
  // (#1148, Kiran #15).
  powerDistributionConfigurations: 'Power Distribution',
};

/**
 * VH-scoped parent-field → section overrides (#1148, Kiran #16 + round-4 follow-ups). These
 * Large UPS and Power Distribution scalar fields are registered twice in `fieldMapping` —
 * first under "Facility Power Readiness", later under their own section (#1330) — so
 * `getMappingByFormField` first-match returns the wrong section for scope-clear parent deltas.
 * Force each to its real section so add-unit and delete-scope agree.
 *
 * The full column sets for each step live in the server's `REQUEST_STEP_CLEAR_COLUMNS`; the
 * camelCase field names below mirror them 1:1 (snake_case → camelCase). Kiran round-4 flagged
 * `powerSourceLocation` + `powerFireSuppressionNeeds` still resolving to Facility Power
 * (Issue 16 incomplete) and the entire Power Distribution set mislabeled (F-1), so both full
 * sets are enumerated here rather than the earlier partial Large UPS list.
 * (`locationTagId` is intentionally excluded — it's a shared unit field, not a scalar.)
 */
const PARENT_FIELD_SECTION_OVERRIDE: Record<string, string> = {
  // Large UPS (server step 'large-ups')
  powerExistingSiteProtectedByLargeKwUps: 'Large UPS',
  powerModelOfExistingUps: 'Large UPS',
  powerLevelOfRedundancy: 'Large UPS',
  powerRequiredRedundancyForUps: 'Large UPS',
  powerBatteryRuntimeRequiredMin: 'Large UPS',
  powerBatteryChemistry: 'Large UPS',
  powerSourceLocation: 'Large UPS',
  powerLargeUpsDescription: 'Large UPS',
  powerFireSuppressionNeeds: 'Large UPS',
  powerTypesOfUps: 'Large UPS',
  powerInputPowerPhase: 'Large UPS',
  powerRuntime: 'Large UPS',
  acOrDcPower: 'Large UPS', // mapper drops the `power_` prefix — NOT powerAcOrDcPower
  // Power Distribution (server step 'power-distribution')
  powerDistributionVoltageToLoad: 'Power Distribution',
  powerOverheadBuswayOrUnderFloor: 'Power Distribution',
  powerOutletSwitchingOrMonitored: 'Power Distribution',
  powerBuswayAmperage: 'Power Distribution',
  powerBuswayLengthFt: 'Power Distribution',
  powerUpsOutputVoltage: 'Power Distribution',
  powerTapOfBoxRequirements: 'Power Distribution',
};

/** Attachment delta keys — must match `request-version.service.ts`. */
const ATTACHMENT_FIELD = 'attachments';
const ATTACHMENT_CAPTION_FIELD = 'attachmentCaption';
/** Where attachment changes belong, as the step registry names it. */
const ATTACHMENT_SECTION = 'Supporting Documents';

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined;

const VERSION_SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  import_audio: 'Audio',
  import_document_pdf: 'Document (PDF)',
  import_document_image: 'Document (Image)',
  baseline: 'Baseline',
  restore_full: 'Restored',
  restore_partial: 'Restored',
  offline_sync: 'Offline sync',
  attachment: 'Attachment',
};

const humanize = (key = ''): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, c => c.toUpperCase())
    .replace(/\bId\b/g, 'ID')
    .trim();

/** Badge text for a version's source (defaults to "Manual"). */
export const versionSourceLabel = (
  source?: RequestVersionSource | string | null,
): string => (source && VERSION_SOURCE_LABELS[source]) || 'Manual';

export interface DeltaDisplay {
  sectionName: string;
  fieldName: string;
}

/**
 * Field-label overrides for id-valued unit fields that have a clean UI label users know
 * (Kiran #5). `locationTagId` is the unit's **Location** assignment; its value is resolved
 * from the tag id to the tag name by the change table (see `resolveLocationTagValue`).
 */
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  locationTagId: 'Location',
};

/** True for the unit-Location field whose values need id → tag-name resolution (Kiran #5). */
export const isLocationField = (field?: string): boolean =>
  field === 'locationTagId';

/**
 * UI-facing field label. Resolution order (#1148 Issue 22):
 *   1. `FIELD_LABEL_OVERRIDES` — hand overrides (e.g. locationTagId → Location).
 *   2. `getRegistryFieldLabel` — the real per-field label from the request field registry
 *      (`*.fields.ts`), keyed by `dataField`. THIS is the label users see on the form.
 *   3. `fieldMapping.jsonField` — fallback ONLY. `jsonField` is an RBAC permission bucket that
 *      several distinct fields share (e.g. Manufacturer/Model/Serial → "UPS Make/Model"), so using
 *      it as a label mislabels and duplicates bucketed sections (Small UPS). Kept only for the few
 *      fields with no registry row.
 * Returns null when nothing matches (caller falls back to the server label / humanized key).
 */
const registryLabel = (field?: string): string | null =>
  (field &&
    (FIELD_LABEL_OVERRIDES[field] ||
      getRegistryFieldLabel(field) ||
      fieldMappingService.getMappingByFormField(field)?.jsonField)) ||
  null;

const UNIT_COLLECTION: Record<string, string> = {
  rackDimensionsLength: 'rackConfigurations',
  rackDimensionsWidth: 'rackConfigurations',
  coolingTempRisePrefThroughItEquipment: 'coolingSystemConfigurations',
  powerBuswayLengthFt: 'powerDistributionConfigurations',
};

const INSTANCE_UNIT_KEY = /^([A-Za-z][A-Za-z0-9]*)#(-?\d+)$/;

const stripUnitBracket = (label: string): string =>
  label
    .replace(
      /\s*[\[(](?:ft\/in|lbs\/sqft|lb\/sqft|kg\/m2|ft|in|mm|m|F|C)[\])]\s*$/i,
      '',
    )
    .trim();

const unitRowKey = (delta: RequestVersionDelta): string =>
  `${delta.scope}:${delta.arrayKey ?? ''}:${String(delta.rowId ?? '')}:${
    delta.field ?? ''
  }`;

const labelWithUnit = (field: string, code: string): string =>
  `${stripUnitBracket(
    getRegistryFieldLabel(field) || humanize(field),
  )} (${code})`;

/**
 * A dimensionUnits save stores every chip. Show one row per chip that changed,
 * with the previous unit and the new unit, not the whole map. The field name
 * uses the unit selected in that save, including on a number-only edit.
 */
export const expandDimensionUnitDeltas = (
  deltas: RequestVersionDelta[],
  savedUnits?: unknown,
): RequestVersionDelta[] => {
  const selectedLabel = new Map<string, string>();
  const expanded = deltas.flatMap(delta => {
    if (delta.field !== 'dimensionUnits' || delta.action !== 'update') {
      return [delta];
    }
    const previous = parseDimensionUnits(delta.oldValue);
    const current = parseDimensionUnits(delta.newValue);
    const keys = new Set([...Object.keys(previous), ...Object.keys(current)]);
    const rows: RequestVersionDelta[] = [];
    for (const key of keys) {
      const oldCode = historyUnitCode(delta.oldValue, key);
      const newCode = historyUnitCode(delta.newValue, key);
      if (!oldCode || !newCode || oldCode === newCode) continue;
      const match = INSTANCE_UNIT_KEY.exec(key);
      const field = match?.[1] ?? key;
      const historyLabel = labelWithUnit(field, newCode);
      const collection = UNIT_COLLECTION[field];
      const row: RequestVersionDelta =
        match && collection
          ? {
              scope: 'child',
              arrayKey: collection,
              rowId: match[2],
              field,
              historyLabel,
              oldValue: oldCode,
              newValue: newCode,
              action: 'update',
            }
          : {
              scope: 'parent',
              field,
              historyLabel,
              oldValue: oldCode,
              newValue: newCode,
              action: 'update',
            };
      selectedLabel.set(unitRowKey(row), historyLabel);
      rows.push(row);
    }
    return rows.length > 0 ? rows : [delta];
  });

  return expanded.map(delta => {
    if (delta.historyLabel) return delta;
    const selected = selectedLabel.get(unitRowKey(delta));
    if (selected) return { ...delta, historyLabel: selected };
    if (
      savedUnits == null ||
      delta.action !== 'update' ||
      !delta.field ||
      !isDimensionField(delta.field)
    ) {
      return delta;
    }
    const storageKey =
      delta.scope === 'child' && delta.rowId != null
        ? `${delta.field}#${delta.rowId}`
        : delta.field;
    const code = historyUnitCode(savedUnits, storageKey);
    return code
      ? { ...delta, historyLabel: labelWithUnit(delta.field, code) }
      : delta;
  });
};

/** Resolve the SECTION NAME + FIELD NAME shown in a version's change table. */
export const resolveDeltaDisplay = (
  delta: RequestVersionDelta,
): DeltaDisplay => {
  const baseLabel =
    delta.historyLabel ||
    registryLabel(delta.field) ||
    delta.label ||
    humanize(delta.field);

  if (delta.scope === 'team') {
    const mapping = delta.field
      ? fieldMappingService.getMappingByFormField(delta.field)
      : null;
    return {
      sectionName: mapping?.formSection || 'Team Information',
      fieldName: baseLabel,
    };
  }

  if (delta.scope === 'child') {
    const sectionName =
      (delta.arrayKey && ARRAY_KEY_TO_SECTION[delta.arrayKey]) ||
      humanize((delta.arrayKey || '').replace(/Configurations$/, ''));
    const rowLabel =
      delta.rowLabel != null && String(delta.rowLabel).length > 0
        ? String(delta.rowLabel)
        : delta.rowId != null
        ? `#${delta.rowId}`
        : '';

    let fieldName = baseLabel;
    if (delta.action === 'reorder') {
      fieldName = 'Row order';
    } else if (delta.action === 'create') {
      fieldName = rowLabel ? `${rowLabel} (added)` : 'Row added';
    } else if (delta.action === 'delete') {
      fieldName = rowLabel ? `${rowLabel} (removed)` : 'Row removed';
    } else if (rowLabel) {
      fieldName = `${rowLabel} — ${baseLabel}`;
    }
    return { sectionName, fieldName };
  }

  // Scope add/remove (#1501, Kiran Issue 3): the section IS the scope, and the
  // row says what happened to it — mirroring the child `{unit} (added)` rows.
  if (delta.field === 'enabledStepIds') {
    const stepTitle =
      delta.rowLabel != null && String(delta.rowLabel).length > 0
        ? String(delta.rowLabel)
        : 'Scope';
    return {
      sectionName: stepTitle,
      fieldName: delta.action === 'create' ? 'Scope added' : 'Scope removed',
    };
  }

  /**
   * Attachments (#1575 issue 2 captures them, #1507 issue 9 made them legible).
   *
   * ⚠️ DEFENCE IN DEPTH, NOT THE NORMAL PATH. Since #1575 issue 10, the version
   * LIST excludes `source = 'attachment'` server-side, so Revision History does
   * not receive these deltas any more. The field-history endpoint reads a
   * different repository method and is not filtered, so the shape can still
   * arrive — and if it does it must not render as "Attachments" with the file
   * name on both sides, which is the bug this branch exists to make impossible.
   * Same posture as `maskVersionHistoryDeltasForRoles` for #1543.
   *
   * ⚠️ A FILE AND ITS CAPTION ARE DIFFERENT CHANGES. Adding or removing a file
   * has no before/after — the name IS the change — so it reads as `(added)` /
   * `(removed)`, the same convention as a child row. A caption has a real pair,
   * so it keeps the `{row} — {label}` form and shows both values. Both used to
   * arrive as "Attachments" with the file name on both sides, which rendered as
   * `power.jpg → power.jpg` and said nothing.
   */
  if (
    delta.field === ATTACHMENT_FIELD ||
    delta.field === ATTACHMENT_CAPTION_FIELD
  ) {
    const file =
      delta.rowLabel != null && String(delta.rowLabel).length > 0
        ? String(delta.rowLabel)
        : nonEmptyString(delta.newValue) ??
          nonEmptyString(delta.oldValue) ??
          '';

    if (delta.field === ATTACHMENT_CAPTION_FIELD) {
      return {
        sectionName: ATTACHMENT_SECTION,
        fieldName: file ? `${file} — Caption` : 'Caption',
      };
    }
    return {
      sectionName: ATTACHMENT_SECTION,
      fieldName:
        delta.action === 'create'
          ? file
            ? `${file} (added)`
            : 'Attachment added'
          : file
          ? `${file} (removed)`
          : 'Attachment removed',
    };
  }

  // parent
  const mapping = delta.field
    ? fieldMappingService.getMappingByFormField(delta.field)
    : null;
  return {
    sectionName:
      (delta.field && PARENT_FIELD_SECTION_OVERRIDE[delta.field]) ||
      mapping?.formSection ||
      'Request',
    fieldName: baseLabel,
  };
};

/** Partner/team access levels → the same labels users see in the UI (Kiran #8). */
const ACCESS_LABELS: Record<string, string> = {
  read_write: 'Read/Write',
  read: 'Read',
};

/** Object keys never shown inside a value summary (identity / FK / system metadata). */
const EXCLUDED_OBJECT_KEYS = new Set<string>([
  'id',
  'requestId',
  'displayOrder',
  'rowLabel',
  'createdAt',
  'updatedAt',
  'created_at',
  'updated_at',
  'sysModifiedOn',
  'sysModifiedBy',
  'sysCreatedOn',
  'sysCreatedBy',
]);

const isEmpty = (v: unknown): boolean =>
  v === null ||
  v === undefined ||
  v === '' ||
  (Array.isArray(v) && v.length === 0);

/** A team/partner member object → "email (Access)" (Kiran #8). */
const formatMember = (item: Record<string, unknown>): string => {
  const email = item.email != null ? String(item.email) : '';
  const access = ACCESS_LABELS[String(item.accessLevel ?? '')];
  if (email && access) return `${email} (${access})`;
  return email || '—';
};

/**
 * An object value (child-row payload on add/remove, or a structured field) → a readable,
 * labeled summary of its non-empty content fields (Kiran #1 — never raw JSON). Member objects
 * (with `email`) use the access-aware member format.
 */
const formatObject = (obj: Record<string, unknown>): string => {
  if ('email' in obj) return formatMember(obj);
  const parts: string[] = [];
  for (const [key, val] of Object.entries(obj)) {
    if (EXCLUDED_OBJECT_KEYS.has(key) || isEmpty(val)) continue;
    if (val !== null && typeof val === 'object') continue; // keep the summary flat, no nested JSON
    const display =
      typeof val === 'string' && looksLikeRichHtml(val)
        ? formatRichDeltaPreview(val)
        : String(val);
    parts.push(`${registryLabel(key) || humanize(key)}: ${display}`);
  }
  return parts.length > 0 ? parts.join(', ') : '—';
};

const IMG_TAG = /<img\b[^>]*>/gi;
const ATTACHMENT_SRC = /sitescope-attachment:\/\/(\d+)/i;

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function readAttr(tag: string, name: string): string {
  const match = new RegExp(`\\b${name}=(["'])(.*?)\\1`, 'i').exec(tag);
  return match?.[2]?.trim() ?? '';
}

/** True when a delta string is stored rich HTML, not legacy plain text. */
export const looksLikeRichHtml = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return Boolean(trimmed) && !isLegacyPlainTextAreaValue(trimmed);
};

function filenameForImgTag(
  tag: string,
  namesById?: ReadonlyMap<number, string>,
): string {
  const src = readAttr(tag, 'src');
  const idMatch = ATTACHMENT_SRC.exec(src);
  const attachmentId = idMatch ? Number.parseInt(idMatch[1], 10) : Number.NaN;
  if (Number.isInteger(attachmentId) && namesById?.has(attachmentId)) {
    return namesById.get(attachmentId) ?? 'Image';
  }
  return readAttr(tag, 'alt') || 'Image';
}

function replaceImgTagsWithFilenames(
  html: string,
  namesById?: ReadonlyMap<number, string>,
): string {
  return html.replace(IMG_TAG, tag => {
    const name = filenameForImgTag(tag, namesById);
    return `<span>${escapeHtml(name)}</span>`;
  });
}

/** Sanitized rich HTML for the Old/New modal — same allow-list as Summary (images stay images). */
export const formatRichDeltaHtml = (html: string): string =>
  sanitizeTextArea(html, { allowRemoteHttpImages: true });

/** One-line table preview: no tags; images collapse to a filename. */
export const formatRichDeltaPreview = (
  html: string,
  namesById?: ReadonlyMap<number, string>,
): string => {
  const named = replaceImgTagsWithFilenames(html, namesById);
  const plain = convertToPlainText(sanitizeTextArea(named))
    .replace(/\s+/g, ' ')
    .trim();
  return plain || '—';
};

/** Format a delta value for the table / detail modal. Empty → em-dash; never raw JSON. */
export const formatDeltaValue = (
  value: unknown,
  namesById?: ReadonlyMap<number, string>,
): string => {
  if (isEmpty(value)) return '—';
  if (looksLikeRichHtml(value)) {
    return formatRichDeltaPreview(String(value), namesById);
  }
  if (Array.isArray(value)) {
    const parts = value.map(item =>
      item && typeof item === 'object'
        ? formatObject(item as Record<string, unknown>)
        : String(item),
    );
    return parts.length > 0 ? parts.join(', ') : '—';
  }
  if (typeof value === 'object')
    return formatObject(value as Record<string, unknown>);
  return String(value);
};

/** Whether a formatted value is long enough to underline as truncated (#1555). */
export const isLongValue = (value: string): boolean => value.length > 42;
